"use client";

import { useEffect, useMemo, useState, type FormEvent } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/client/layout/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import useBootstrapClient from "@/features/hooks/useBootstrapClient";

export interface SearchEntry {
  title: string;
  path: string;
  description?: string;
  keywords?: string[];
}

interface SearchPageClientProps {
  locale: string;
  initialQuery?: string;
  entries: SearchEntry[];
}

const normalizePath = (path: string) => (path.startsWith("/") ? path : `/${path}`);

const SearchPageClient = ({ locale, initialQuery = "", entries }: SearchPageClientProps) => {
  useBootstrapClient();
  const { t } = useTranslation(["pages", "common"]);
  const router = useRouter();
  const [query, setQuery] = useState(initialQuery);

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  const normalizedEntries = useMemo(() => {
    const uniqueEntries = new Map<string, SearchEntry>();
    entries.forEach((entry) => {
      if (!entry?.title) {
        return;
      }
      const normalized = normalizePath(entry.path);
      if (uniqueEntries.has(normalized)) {
        return;
      }
      const keywords = entry.keywords?.filter(Boolean);
      uniqueEntries.set(normalized, { ...entry, path: normalized, keywords });
    });
    return Array.from(uniqueEntries.values());
  }, [entries]);

  const filteredEntries = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      return normalizedEntries;
    }

    return normalizedEntries.filter((entry) => {
      const bucket = [
        entry.title,
        entry.description ?? "",
        ...(entry.keywords ?? []),
        entry.path,
      ]
        .filter(Boolean)
        .map((item) => item.toLowerCase());

      return bucket.some((item) => item.includes(term));
    });
  }, [normalizedEntries, query]);

  const baseSearchPath = `/${locale}/search`;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const term = query.trim();
    const params = term ? `?q=${encodeURIComponent(term)}` : "";
    router.replace(`${baseSearchPath}${params}`);
  };

  const handleClear = () => {
    setQuery("");
    router.replace(baseSearchPath);
  };

  const buildHref = (path: string) => {
    const normalized = normalizePath(path);
    return `/${locale}${normalized === "/" ? "" : normalized}`;
  };

  const headerTitle = t("search.header.title");
  const headerDescription = t("search.header.description");
  const placeholder = t("search.form.placeholder") as string;
  const submitLabel = t("search.form.submit") as string;
  const clearLabel =
    (t("search.form.clear", { defaultValue: t("common:actions.clear") }) as string) ||
    (t("common:actions.clear") as string);
  const resultsTitle = t("search.results.title");
  const emptyText = t("search.results.empty");
  const openLabel = t("search.results.open");

  return (
    <PageContainer>
      <div className="flex flex-col gap-4 pb-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold leading-tight">{headerTitle}</h1>
          <p className="text-sm text-muted-foreground">{headerDescription}</p>
        </div>

        <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <div className="flex w-full items-center gap-2">
              <Input
                id="site-search-input"
                name="q"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={placeholder}
                aria-label={placeholder}
              />
              {query ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={clearLabel}
                  onClick={handleClear}
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
            <Button type="submit" className="sm:w-auto" size="sm">
              <Search className="mr-2 h-4 w-4" />
              {submitLabel}
            </Button>
          </div>
        </form>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg font-semibold leading-tight">{resultsTitle}</h2>
            <span className="text-xs text-muted-foreground">
              {filteredEntries.length} / {normalizedEntries.length}
            </span>
          </div>

          {filteredEntries.length === 0 ? (
            <p className="text-sm text-muted-foreground">{emptyText}</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {filteredEntries.map((entry) => (
                <Card key={entry.path} className="h-full">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base leading-tight">{entry.title}</CardTitle>
                    {entry.description ? (
                      <CardDescription className="text-sm leading-relaxed">
                        {entry.description}
                      </CardDescription>
                    ) : null}
                  </CardHeader>
                  <CardContent className="flex flex-wrap items-center gap-2 pt-0">
                    <Button asChild size="sm" variant="secondary">
                      <Link href={buildHref(entry.path)}>{openLabel}</Link>
                    </Button>
                    <Badge variant="outline" className="text-[11px]">
                      {entry.path === "/" ? `/${locale}` : entry.path}
                    </Badge>
                    {(entry.keywords ?? []).map((keyword) => (
                      <Badge key={`${entry.path}-${keyword}`} variant="secondary" className="text-[11px]">
                        {keyword}
                      </Badge>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default SearchPageClient;
export type { SearchPageClientProps };
